import React, { useState } from "react";

import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@mui/material";

import EmailIcon from "@mui/icons-material/Email";
import CallIcon from "@mui/icons-material/Call";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

import { feedbackApi } from "../services/api";

const CONTACT_EMAIL = "contact.swadveda@gmail.com";
const CONTACT_PHONE_DISPLAY = "+91 90******92";
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "";
const WHATSAPP_URL = WHATSAPP_NUMBER
  ? `https://wa.me/${WHATSAPP_NUMBER.replace(/\D/g, "")}`
  : "https://wa.me/?text=Hi%20SwadVeda%2C%20I%20need%20help%20with%20an%20order.";

const contactCategories = [
  { value: "feedback", label: "Feedback" },
  { value: "complaint", label: "Complaint" },
  { value: "dealership_enquiry", label: "Enquiry for dealership" },
  { value: "distributership_enquiry", label: "Enquiry for distributership" }
];

const ContactPage = () => {

  const [formData, setFormData] =
    useState({
      name: "",
      email: "",
      category: "feedback",
      message: ""
    });

  const [loading, setLoading] =
    useState(false);
  const [resultDialog, setResultDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]:
        e.target.value
    });
  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    try {

      setLoading(true);

      await feedbackApi.submit({
        name: formData.name,
        email: formData.email,
        category: formData.category,
        message: formData.message
      });

      setResultDialog({
        open: true,
        type: "success",
        title: "Message sent",
        message: "Thank you for reaching out. Our team will get back to you soon."
      });

      setFormData({
        name: "",
        email: "",
        category: "feedback",
        message: ""
      });

    } catch (error) {

      console.error(error);

      setResultDialog({
        open: true,
        type: "error",
        title: "Could not send message",
        message:
          error?.response?.data?.message ||
          "Failed to submit message. Please try again."
      });

    } finally {

      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        background: "#fff8f2",
        minHeight: "100vh"
      }}
    >
      {/* HERO */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg,#5c0000,#8B0000)",

          py: {
            xs: 8,
            md: 12
          },

          color: "#fff"
        }}
      >
        <Container maxWidth="lg">

          <Typography
            sx={{
              fontSize: {
                xs: "2.8rem",
                md: "5rem"
              },

              fontWeight: 900,

              mb: 3
            }}
          >
            Contact Us
          </Typography>

          <Typography
            sx={{
              maxWidth: 700,

              lineHeight: 1.9,

              fontSize: {
                xs: 16,
                md: 20
              },

              opacity: 0.92
            }}
          >
            Have questions about our products
            or orders? We'd love to hear from
            you.
          </Typography>

        </Container>
      </Box>

      {/* CONTENT */}
      <Container
        maxWidth="lg"
        sx={{
          py: {
            xs: 8,
            md: 12
          }
        }}
      >
        <Grid container spacing={5}>

          {/* LEFT */}
          <Grid item xs={12} md={5}>

            <Typography
              sx={{
                fontSize: {
                  xs: "2rem",
                  md: "3rem"
                },

                fontWeight: 900,

                color: "#3d1f00",

                mb: 3
              }}
            >
              Get In Touch
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",

                lineHeight: 1.9,

                mb: 5
              }}
            >
              Reach out for product inquiries,
              support or partnership
              opportunities.
            </Typography>

            {[
              {
                icon: <EmailIcon />,
                title: "Email",
                value: CONTACT_EMAIL,
                href: `mailto:${CONTACT_EMAIL}`
              },

              {
                icon: <CallIcon />,
                title: "Phone",
                value: CONTACT_PHONE_DISPLAY,
                href: WHATSAPP_URL,
                external: true
              },

              {
                icon: <LocationOnIcon />,
                title: "Address",
                value:
                  "Noida, Uttar Pradesh, India"
              }

            ].map((item) => (

              <Paper
                component={item.href ? "a" : "div"}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                key={item.title}
                elevation={0}
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  p: 3,

                  mb: 3,

                  borderRadius: "24px",

                  display: "flex",

                  gap: 2,

                  alignItems: "center",

                  border:
                    "1px solid rgba(139,0,0,0.08)"
                }}
              >

                <Box
                  sx={{
                    width: 60,

                    height: 60,

                    borderRadius: "18px",

                    background:
                      "linear-gradient(135deg,#8B0000,#5c0000)",

                    color: "#fff",

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center"
                  }}
                >
                  {item.icon}
                </Box>

                <Box>

                  <Typography
                    fontWeight={800}
                    color="#3d1f00"
                    mb={0.5}
                  >
                    {item.title}
                  </Typography>

                  <Typography color="text.secondary">
                    {item.value}
                  </Typography>

                </Box>

              </Paper>
            ))}

          </Grid>

          {/* RIGHT */}
          <Grid item xs={12} md={7}>

            <Paper
              elevation={0}
              sx={{
                p: {
                  xs: 3,
                  md: 5
                },

                borderRadius: "32px",

                background: "#fff",

                border:
                  "1px solid rgba(139,0,0,0.08)"
              }}
            >

              <Typography
                fontWeight={900}
                fontSize="2rem"
                color="#3d1f00"
                mb={4}
              >
                Send Message
              </Typography>

              <form onSubmit={handleSubmit}>

                <Grid container spacing={3}>

                  {/* NAME */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </Grid>

                  {/* EMAIL */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </Grid>

                  {/* CATEGORY */}
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                    >

                      {contactCategories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}

                    </TextField>
                  </Grid>

                  {/* MESSAGE */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={6}
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </Grid>

                  {/* BUTTON */}
                  <Grid item xs={12}>

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      sx={{
                        background:
                          "linear-gradient(135deg,#5c0000,#8B0000)",

                        px: 5,

                        py: 1.6,

                        borderRadius: "14px",

                        textTransform: "none",

                        fontWeight: 800,

                        fontSize: 16,

                        minWidth: 180,

                        "&:hover": {
                          background:
                            "linear-gradient(135deg,#4a0000,#700000)"
                        }
                      }}
                    >
                      {loading
                        ? <CircularProgress size={22} sx={{ color: "#fff" }} />
                        : "Send Message"}
                    </Button>

                  </Grid>

                </Grid>

              </form>

            </Paper>

          </Grid>

        </Grid>
      </Container>

      <Dialog
        open={resultDialog.open}
        onClose={() => setResultDialog((current) => ({ ...current, open: false }))}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            textAlign: "center",
            p: 1
          }
        }}
      >
        <DialogContent sx={{ pt: 4, px: 4 }}>
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                resultDialog.type === "success"
                  ? "rgba(46,125,50,0.12)"
                  : "rgba(198,40,40,0.12)",
              color:
                resultDialog.type === "success"
                  ? "#2e7d32"
                  : "#c62828"
            }}
          >
            {resultDialog.type === "success" ? (
              <CheckCircleRoundedIcon sx={{ fontSize: 42 }} />
            ) : (
              <ErrorOutlineRoundedIcon sx={{ fontSize: 42 }} />
            )}
          </Box>

          <Typography variant="h5" fontWeight={900} color="#3d1f00" mb={1}>
            {resultDialog.title}
          </Typography>

          <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {resultDialog.message}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setResultDialog((current) => ({ ...current, open: false }))}
            sx={{
              background: "linear-gradient(135deg,#5c0000,#8B0000)",
              borderRadius: "14px",
              py: 1.2,
              textTransform: "none",
              fontWeight: 800
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactPage;
